import { Degree } from "@/models/degree";
import { District } from "@/models/district";
import { Minister } from "@/models/minister"
import {
    getAllMinisters as fetchAllMinisters,
    getMinistersPaginated,
    getMinisterById as fetchMinisterById,
    createMinister as addMinister,
    updateMinister as modifyMinister,
    deleteMinister as removeMinister,
    checkMinisterLinkedRecords as checkLinkedRecordsService,
    hardDeleteMinister as hardDeleteMinisterService,
    getInactiveReasons as fetchInactiveReasons,
    inactivateMinister as inactivateMinisterService,
    reactivateMinister as reactivateMinisterService,
    getMinisterHistory as fetchMinisterHistory,
    logDegreeUpdate as logDegreeUpdateService,
    logTransfer as logTransferService,
    changeDegree as changeDegreeService,
    transferMinister as transferMinisterService,
    type MinisterFilters,
    type MinisterLinkedRecords,
} from "@/Services/ministers.service";
import type { InactiveReason, MinisterHistoryEvent } from "@/features/ministers/types";
import { type PaginatedResponse, type SortOption } from "@/Services/supabaseClient";
import { getAllDistrictsWithRegions } from "@/Services/regions.service";
import { getAllDegrees } from "@/Services/degrees.service";
import { Database } from "@/types/supabase";

type MinisterInsert = Database['public']['Tables']['ministers']['Insert'];
type MinisterUpdate = Database['public']['Tables']['ministers']['Update'];

// Async wrapper for ministers - returns promise
const getAllMinisters = async (): Promise<Minister[]> => {
    try {
        const ministers = await fetchAllMinisters();
        return ministers;
    } catch (error) {
        console.error('Error in getAllMinisters:', error);
        return [];
    }
}

// Get single minister by ID
const getMinisterById = async (id: string): Promise<Minister | null> => {
    try {
        return await fetchMinisterById(id);
    } catch (error) {
        console.error('Error in getMinisterById:', error);
        return null;
    }
}

// Paginated ministers with search and filters
const getMinistersPaginatedList = async (
    page: number = 1,
    pageSize: number = 20,
    filters?: MinisterFilters,
    sort?: SortOption
): Promise<PaginatedResponse<Minister>> => {
    try {
        return await getMinistersPaginated(page, pageSize, filters, sort);
    } catch (error) {
        console.error('Error in getMinistersPaginatedList:', error);
        return {
            data: [],
            count: 0,
            page,
            pageSize,
            totalPages: 0
        };
    }
}

const createMinister = async (minister: MinisterInsert): Promise<Minister | null> => {
    try {
        return await addMinister(minister);
    } catch (error) {
        console.error('Error in createMinister:', error);
        throw error;
    }
}

const updateMinister = async (id: string, minister: MinisterUpdate): Promise<Minister | null> => {
    try {
        return await modifyMinister(id, minister);
    } catch (error) {
        console.error('Error in updateMinister:', error);
        throw error;
    }
}

const deleteMinister = async (id: string): Promise<boolean> => {
    try {
        await removeMinister(id);
        return true;
    } catch (error) {
        console.error('Error in deleteMinister:', error);
        return false;
    }
}

const checkMinisterLinks = async (id: string): Promise<MinisterLinkedRecords> => {
    return checkLinkedRecordsService(id);
}

const hardDeleteMinister = async (id: string): Promise<boolean> => {
    try {
        await hardDeleteMinisterService(id);
        return true;
    } catch (error) {
        console.error('Error in hardDeleteMinister:', error);
        return false;
    }
}

const getDegreesList = async (): Promise<Degree[]> => {
    try {
        return await getAllDegrees();
    } catch (error) {
        console.error('Error in getAllDegrees:', error);
        return [];
    }
}

const getInactiveReasons = async (): Promise<InactiveReason[]> => {
    try {
        return await fetchInactiveReasons();
    } catch (error) {
        console.error('Error in getInactiveReasons:', error);
        return [];
    }
}

const inactivateMinister = async (
    ministerId: string,
    reasonCode: string,
    eventDate: string,
    userId: string,
    notes?: string
): Promise<boolean> => {
    try {
        await inactivateMinisterService(ministerId, reasonCode, eventDate, userId, notes);
        return true;
    } catch (error) {
        console.error('Error in inactivateMinister:', error);
        throw error;
    }
}

const reactivateMinister = async (
    ministerId: string,
    eventDate: string,
    userId: string,
    notes?: string
): Promise<boolean> => {
    try {
        await reactivateMinisterService(ministerId, eventDate, userId, notes);
        return true;
    } catch (error) {
        console.error('Error in reactivateMinister:', error);
        throw error;
    }
}

const getMinisterHistory = async (ministerId: string): Promise<MinisterHistoryEvent[]> => {
    try {
        return await fetchMinisterHistory(ministerId);
    } catch (error) {
        console.error('Error in getMinisterHistory:', error);
        return [];
    }
}

const logDegreeUpdate = async (
    ministerId: string,
    degreeFrom: string | null,
    degreeTo: string,
    eventDate: string,
    userId: string,
    notes?: string
): Promise<void> => {
    try {
        await logDegreeUpdateService(ministerId, degreeFrom, degreeTo, eventDate, userId, notes);
    } catch (error) {
        console.error('Error in logDegreeUpdate:', error);
        throw error;
    }
}

const logTransfer = async (
    ministerId: string,
    districtFrom: string | null,
    districtTo: string | null,
    regionFrom: string | null,
    regionTo: string | null,
    eventDate: string,
    userId: string,
    notes?: string
): Promise<void> => {
    try {
        await logTransferService(ministerId, districtFrom, districtTo, regionFrom, regionTo, eventDate, userId, notes);
    } catch (error) {
        console.error('Error in logTransfer:', error);
        throw error;
    }
}

const changeDegree = async (
    ministerId: string,
    degreeFrom: string | null,
    degreeTo: string,
    eventDate: string,
    userId: string,
    notes?: string
): Promise<void> => {
    try {
        await changeDegreeService(ministerId, degreeFrom, degreeTo, eventDate, userId, notes);
    } catch (error) {
        console.error('Error in changeDegree:', error);
        throw error;
    }
}

const transferMinister = async (
    ministerId: string,
    districtFrom: string | null,
    districtTo: string,
    regionFrom: string | null,
    regionTo: string,
    eventDate: string,
    userId: string,
    notes?: string
): Promise<void> => {
    try {
        await transferMinisterService(ministerId, districtFrom, districtTo, regionFrom, regionTo, eventDate, userId, notes);
    } catch (error) {
        console.error('Error in transferMinister:', error);
        throw error;
    }
}

const getAllDistrictsAndRegions = async (): Promise<District[]> => {
    try {
        const districts = await getAllDistrictsWithRegions();
        
        // Sort by code
        return districts.sort((a, b) => {
            const codeA = a.code || '';
            const codeB = b.code || '';
            if (codeA < codeB) return -1;
            if (codeA > codeB) return 1;
            return 0;
        });
    } catch (error) {
        console.error('Error in getAllDistrictsAndRegions:', error);
        return [];
    }
}

export {
    getAllMinisters,
    getMinisterById,
    getMinistersPaginatedList,
    createMinister,
    updateMinister,
    deleteMinister,
    checkMinisterLinks,
    hardDeleteMinister,
    getInactiveReasons,
    inactivateMinister,
    reactivateMinister,
    getMinisterHistory,
    logDegreeUpdate,
    logTransfer,
    changeDegree,
    transferMinister,
    getDegreesList,
    getAllDistrictsAndRegions,
}

export type { MinisterLinkedRecords };

// Re-export types for convenience
export type { MinisterFilters } from "@/Services/ministers.service";
export type { PaginatedResponse, SortOption } from "@/Services/supabaseClient";